<template>
    <div class="post-view">
        <div class="post-container">
            <h1>{{ post.username || 'Anonyme' }}</h1>
            <div class="post-meta">
                <span class="country">{{ $t(`allCountry.${post.country}`, post.country) }}</span>
                <span class="theme">{{ $t(`allTheme.${post.theme}`, post.theme) }}</span>
                <span class="date">{{ formatDate(post.timestamp) }}</span>
            </div>
            <div class="post-content">{{ post.content }}</div>
            <img v-if="postImageUrl" :src="postImageUrl" alt="Post image" class="post-image" loading="lazy">
        </div>
    </div>
</template>

<script>
import apiService from '@/services/apiService';
import moment from 'moment';

export default {
    data() {
        return {
            post: {},
            postImageUrl: '',
            imageGenerationPromise: null
        };
    },
    // Dans PostView.vue
    async created() {
        try {
            const postId = this.$route.params.id;
            this.post = await apiService.getPostById(postId);

            // Mettre à jour les meta tags immédiatement avec les infos de base
            this.updateMetaTags();

            // Générer l'image ensuite
            this.imageGenerationPromise = this.generatePostImage(this.post)
                .then(url => {
                    this.postImageUrl = url;
                    // Mettre à jour à nouveau les meta tags avec l'URL de l'image
                    this.updateMetaTags();
                })
                .catch(error => {
                    console.error("Image generation failed:", error);
                });

        } catch (error) {
            console.error("Failed to load post:", error);
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('LL');
        },
        async generatePostImage(post) {
            if (!post?.id) return '';

            const imageName = `post-${post.id}.png`;
            const imagePath = `/images/posts/${imageName}`;

            // Vérification de l'existence de l'image
            try {
                const response = await fetch(imagePath, { method: 'HEAD' });
                if (response.ok) return imagePath;
            } catch (e) {
                console.log("Image doesn't exist, generating new one");
            }

            // Création de l'iframe
            const iframe = document.createElement('iframe');
            Object.assign(iframe.style, {
                position: 'fixed',
                left: '-9999px',
                visibility: 'hidden',
                width: '500px', // Modifié pour correspondre à l'autre version
                height: '500px', // Modifié pour correspondre à l'autre version
                border: 'none'
            });

            document.body.appendChild(iframe);

            try {
                // Écrire le contenu dans l'iframe
                const doc = iframe.contentDocument || iframe.contentWindow.document;
                doc.open();
                doc.write(this.generateImageHTML(post));
                doc.close();

                // Attendre que l'iframe soit chargé
                await new Promise((resolve) => {
                    iframe.addEventListener('load', resolve, { once: true });
                    setTimeout(resolve, 2000);
                });

                // Générer l'image avec html2canvas
                const html2canvas = await import('html2canvas');
                const canvas = await html2canvas.default(iframe.contentDocument.body, {
                    width: 500, // Correspond à la taille de l'iframe
                    height: 500, // Correspond à la taille de l'iframe
                    scale: 1,
                    logging: false,
                    useCORS: true,
                    backgroundColor: '#121212',
                    allowTaint: true,
                    foreignObjectRendering: true
                });

                // Convertir en blob et sauvegarder
                const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.9));
                await apiService.savePostImage(blob, imageName);

                return imagePath;
            } catch (error) {
                console.error("Error generating image:", error);
                throw error;
            } finally {
                document.body.removeChild(iframe);
            }
        },

        generateImageHTML(post) {
            return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Cry4Change - Témoignage</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Arial', sans-serif; }
    body {
      background-color: #121212;
      color: #f0f0f0;
      height: 500px;
      width: 500px;
      position: relative;
      overflow: hidden;
      margin: 0;
      padding: 30px;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      border: 15px solid #1d1d1b;
    }
    .header { display: flex; align-items: center; margin-bottom: 30px; }
    .icon { font-size: 28px; margin-right: 15px; color: #e03a3a; }
    .post-info { font-size: 14px; color: #aaa; }
    .content {
      font-size: 20px;
      line-height: 1.6;
      margin-bottom: 30px;
      padding: 20px;
      background: rgba(30, 30, 30, 0.7);
      border-left: 3px solid #e03a3a;
    }
    .footer {
      position: absolute;
      bottom: 30px;
      left: 0;
      width: 100%;
      padding: 0 30px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .country { display: flex; align-items: center; font-size: 14px; color: #aaa; }
    .country-icon { margin-right: 5px; font-size: 16px; }
    .signature { font-size: 14px; color: #e03a3a; font-weight: bold; }
    .theme {
      background: #e03a3a;
      color: white;
      padding: 3px 8px;
      border-radius: 3px;
      font-size: 12px;
      margin-top: 10px;
      display: inline-block;
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="icon">📢</div>
    <div class="post-info">
      <div>${post.is_anonymous == 1 ? this.$t('anonymous') : this.escapeHtml(post.username || 'Anonyme')}</div>
      <div class="theme">${this.escapeHtml(this.$t(`allTheme.${post.theme}`, post.theme))}</div>
    </div>
  </div>
  <div class="content">
    ${this.escapeHtml(post.content).replace(/\n/g, '<br>')}
  </div>
  <div class="footer">
    <div class="country">
      <span class="country-icon">📍</span>
      ${this.escapeHtml(this.$t(`allCountry.${post.country}`, post.country))}
    </div>
    <div class="signature">cry4change.com</div>
  </div>
</body>
</html>
    `;
        },

        escapeHtml(unsafe) {
            return unsafe
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        },
        updateMetaTags() {
            const fullImageUrl = this.postImageUrl
                ? `${window.location.origin}${this.postImageUrl}`
                : `${window.location.origin}/images/default-social-image.png`;

            const description = this.post.content
                ? this.post.content.substring(0, 160)
                : 'Partagez votre témoignage sur Cry4Change';

            const title = `Cry4Change - Post de ${this.post.username || 'Anonyme'}`;

            // Mettre à jour le titre de la page
            document.title = title;

            // Meta tags de base
            const baseTags = [
                // Standard
                { name: 'description', content: description },

                // Open Graph
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:image', content: fullImageUrl },
                { property: 'og:url', content: window.location.href },
                { property: 'og:type', content: 'article' },

                // Twitter
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', content: title },
                { name: 'twitter:description', content: description },
                { name: 'twitter:image', content: fullImageUrl },

                //other
                { name: 'twitter:site', content: '@cry4change' },
                { name: 'twitter:creator', content: '@cry4change' },
                { property: 'article:published_time', content: this.post.timestamp },
                { property: 'article:author', content: this.post.username || 'Anonyme' }
            ];

            // Ajouter les dimensions si l'image est disponible
            if (this.postImageUrl) {
                baseTags.push(
                    { property: 'og:image:width', content: '500' },
                    { property: 'og:image:height', content: '500' }
                );
            }

            // Mettre à jour ou créer les tags
            baseTags.forEach(tag => {
                let selector = '';
                if (tag.property) {
                    selector = `meta[property="${tag.property}"]`;
                } else {
                    selector = `meta[name="${tag.name}"]`;
                }

                let element = document.querySelector(selector);

                if (!element) {
                    element = document.createElement('meta');
                    if (tag.property) {
                        element.setAttribute('property', tag.property);
                    } else {
                        element.setAttribute('name', tag.name);
                    }
                    document.head.appendChild(element);
                }

                element.setAttribute('content', tag.content);
            });
        }
    }
};
</script>

<style scoped>
.post-view {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

.post-container {
    background: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
}

.post-meta {
    color: #666;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    font-size: 0.9rem;
}

.post-content {
    white-space: pre-line;
    margin-bottom: 25px;
    line-height: 1.6;
    font-size: 1.1rem;
}

.post-image {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin-top: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
    .post-view {
        padding: 15px;
    }

    .post-container {
        padding: 20px;
    }
}
</style>